import React, { useState, useEffect, useRef } from "react";
import { MdFitnessCenter, MdDragHandle, MdAdd, MdDelete } from "react-icons/md";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ExerciseService } from "../../services/exerciseService";
import { UpdateActivityLogInput } from "../../types/exercise";
import { getYouTubeThumbnailFromUrl } from "../../utils/youtube";
import {
  getResistanceBandLevelOptions,
  cleanSetsDataByTrainingMethod,
} from "../../utils/exerciseUtils";
import {
  processSetField,
  newSetForTrainingMethod,
  removeSetAndRenumber,
  SET_FIELD_NAMES,
  SetsData,
} from "../../utils/setsValidation";
import TrainingMethodBadge from "../badges/TrainingMethodBadge";
import PersistentActionConfirmationModal from "../modals/PersistentActionConfirmationModal";
import DurationInput from "../fields/DurationInput";

interface SortableExerciseItemProps {
  exercise: any;
  activityLog: any;
  onExerciseClick: (id: number) => void;
}

// One draggable exercise row on the training-day page, with its sets editor.
const SortableExerciseItem: React.FC<SortableExerciseItemProps> = ({
  exercise,
  activityLog,
  onExerciseClick,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: exercise.activityLogId.toString() });

  const [setsData, setSetsData] = useState<SetsData>(
    activityLog?.sets_data || {}
  );
  const [restSeconds, setRestSeconds] = useState<number | null>(
    activityLog?.rest_time_seconds ?? null
  );
  const [rir, setRir] = useState<number | null>(activityLog?.rir ?? null);
  const [rpe, setRpe] = useState<number | null>(activityLog?.rpe ?? null);
  const [notes, setNotes] = useState<string>(activityLog?.notes || "");

  const [originalRestSeconds, setOriginalRestSeconds] = useState<number | null>(
    activityLog?.rest_time_seconds ?? null
  );
  const [originalRir, setOriginalRir] = useState<number | null>(
    activityLog?.rir ?? null
  );
  const [originalRpe, setOriginalRpe] = useState<number | null>(
    activityLog?.rpe ?? null
  );
  const [restSecondsModified, setRestSecondsModified] = useState(false);
  const [rirModified, setRirModified] = useState(false);
  const [rpeModified, setRpeModified] = useState(false);

  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [showPersistenceModal, setShowPersistenceModal] = useState(false);

  // Resync from the server row when the parent refetches: the row is keyed on
  // a stable id, so it never remounts. Refs keep the dirty check out of the
  // dependency list: a save flips hasChanges to false without a new
  // activityLog, and must not reseed.
  const dirtyRef = useRef({ hasChanges, saving });
  dirtyRef.current = { hasChanges, saving };
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    if (dirtyRef.current.hasChanges || dirtyRef.current.saving) return;

    setSetsData(activityLog?.sets_data || {});
    setRestSeconds(activityLog?.rest_time_seconds ?? null);
    setRir(activityLog?.rir ?? null);
    setRpe(activityLog?.rpe ?? null);
    setNotes(activityLog?.notes || "");
    setOriginalRestSeconds(activityLog?.rest_time_seconds ?? null);
    setOriginalRir(activityLog?.rir ?? null);
    setOriginalRpe(activityLog?.rpe ?? null);
    setRestSecondsModified(false);
    setRirModified(false);
    setRpeModified(false);
    setValidationErrors({});
    setSaveError(null);
  }, [activityLog]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
    cursor: "default",
  };

  const handleSetChange = (setKey: string, field: string, value: any) => {
    const fieldKey = `${setKey}_${field}`;
    const { value: processedValue, error: validationError } = processSetField(
      exercise.training_method,
      field,
      value
    );

    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      if (validationError) {
        newErrors[fieldKey] = validationError;
      } else {
        delete newErrors[fieldKey];
      }
      return newErrors;
    });

    setSetsData({
      ...setsData,
      [setKey]: {
        ...setsData[setKey],
        [field]: processedValue,
      },
    });
    setHasChanges(true);
    setSaveError(null);
  };

  const addSet = () => {
    const newSetKey = `set_${Object.keys(setsData).length + 1}`;
    setSetsData({
      ...setsData,
      [newSetKey]: newSetForTrainingMethod(exercise.training_method),
    });
    setHasChanges(true);
    setSaveError(null);
  };

  const removeSet = (setKey: string) => {
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      SET_FIELD_NAMES.forEach(
        (field) => delete newErrors[`${setKey}_${field}`]
      );
      return newErrors;
    });

    setSetsData(removeSetAndRenumber(setsData, setKey));
    setHasChanges(true);
    setSaveError(null);
  };

  const initiatesSave = () => {
    // Check for validation errors before saving
    if (Object.keys(validationErrors).length > 0) {
      setSaveError("Please fix validation errors before saving");
      return;
    }

    setShowPersistenceModal(true);
  };

  const saveSetsData = async (isPersistent: boolean) => {
    setSaving(true);
    setSaveError(null);
    try {
      // Update the specific user_exercise_activity_log record
      if (activityLog?.id) {
        const cleanedSetsData = cleanSetsDataByTrainingMethod(
          setsData,
          exercise.training_method
        );

        const payload: UpdateActivityLogInput = {
          sets_data: cleanedSetsData,
          is_persistent: isPersistent,
        };

        const normalizedRestSeconds =
          restSeconds !== null ? Math.max(0, Math.round(restSeconds)) : null;

        if (restSecondsModified || originalRestSeconds !== null) {
          payload.rest_time_seconds =
            normalizedRestSeconds !== null ? normalizedRestSeconds : 0;
        }

        if (rirModified || originalRir !== null) {
          payload.rir = rir;
        }

        if (rpeModified || originalRpe !== null) {
          payload.rpe = rpe;
        }

        const trimmedNotes = notes.trim();
        if (trimmedNotes || activityLog.notes) {
          payload.notes = trimmedNotes || null;
        }

        const result = await ExerciseService.updateActivityLog(
          activityLog.id,
          payload
        );
        if (!result.success) {
          setSaveError(result.error);
          return;
        }

        setHasChanges(false);
        setShowPersistenceModal(false);
      } else {
        setSaveError("Cannot save: No activity log found for this exercise");
      }
    } catch (error: any) {
      const errorMessage =
        error.message || "Failed to save sets data. Please try again.";
      setSaveError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const setKeys = Object.keys(setsData).sort();

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="cursor-default"
    >
      <div className="rounded-lg border border-gray-200 p-4 transition-colors dark:border-gray-700 dark:bg-navy-700">
        <div>
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0">
              <div
                className="h-20 w-28 cursor-pointer"
                onClick={() => onExerciseClick(exercise.id)}
              >
                {exercise.yt_link &&
                getYouTubeThumbnailFromUrl(exercise.yt_link) ? (
                  <img
                    src={getYouTubeThumbnailFromUrl(exercise.yt_link)}
                    alt={`${exercise.name} thumbnail`}
                    className="h-20 w-28 rounded-lg object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = "none";
                      target.nextElementSibling?.classList.remove("hidden");
                    }}
                  />
                ) : (
                  <div className="flex h-20 w-28 items-center justify-center rounded-lg bg-biafit-primary text-white">
                    <MdFitnessCenter className="h-6 w-6" />
                  </div>
                )}
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <h3
                className="text-md mb-0 inline-block cursor-pointer font-medium text-navy-700 transition duration-150 hover:text-biafit-primary dark:text-white dark:hover:text-biafit-primary"
                onClick={() => onExerciseClick(exercise.id)}
              >
                {exercise.name}
              </h3>
              {/* Training Method Badge */}
              <div className="mb-1">
                <TrainingMethodBadge
                  trainingMethod={exercise.training_method}
                />
              </div>
            </div>

            {/* Drag Handle */}
            <div
              className="mt-2 flex flex-shrink-0 items-center justify-center self-start"
              {...listeners}
            >
              <div className="flex cursor-grab items-center justify-center p-2 text-gray-400 transition-colors hover:text-gray-600 active:cursor-grabbing dark:hover:text-gray-300">
                <MdDragHandle className="h-5 w-5" />
              </div>
            </div>
          </div>

          {/* Sets Section */}
          <div className="mt-4 space-y-2">
            {/* Rest Seconds, RIR, RPE, and Notes */}
            <div className="flex flex-col gap-2 rounded-lg bg-gray-50 px-4 py-2 dark:bg-navy-800">
              <div className="flex items-start gap-3">
                <span className="w-8 flex-shrink-0 pt-1 text-xs font-medium text-gray-500 md:pt-0">
                  All
                </span>
                <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-start md:gap-3">
                  {/* RIR */}
                  <div className="flex flex-col">
                    <label className="flex items-center text-xs font-medium text-gray-700 dark:text-gray-300 md:min-h-[36px] md:text-sm">
                      RIR
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="10"
                      step="1"
                      placeholder="-"
                      value={rir !== null ? rir : ""}
                      onKeyDown={(e) => {
                        if (
                          e.key === "-" ||
                          e.key === "+" ||
                          e.key === "e" ||
                          e.key === "E" ||
                          e.key === "."
                        ) {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => {
                        const val = e.target.value;
                        setRirModified(true);
                        setRpeModified(true);
                        if (val === "") {
                          setRir(null);
                          setRpe(null);
                        } else {
                          const numVal = parseInt(val);
                          const clampedRir = Math.min(10, Math.max(0, numVal));
                          setRir(clampedRir);
                          setRpe(10 - clampedRir);
                        }
                        setHasChanges(true);
                        setSaveError(null);
                      }}
                      className="w-16 rounded border border-gray-300 bg-white p-1 text-xs text-navy-700 placeholder:text-gray-400 dark:border-gray-700 dark:bg-navy-900 dark:text-white dark:[color-scheme:dark] dark:placeholder:text-gray-700 md:w-20 md:p-2 md:text-sm"
                    />
                  </div>

                  {/* RPE */}
                  <div className="flex flex-col">
                    <label className="flex items-center text-xs font-medium text-gray-700 dark:text-gray-300 md:min-h-[36px] md:text-sm">
                      RPE
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="10"
                      step="1"
                      placeholder="-"
                      value={rpe !== null ? rpe : ""}
                      onKeyDown={(e) => {
                        if (
                          e.key === "-" ||
                          e.key === "+" ||
                          e.key === "e" ||
                          e.key === "E" ||
                          e.key === "."
                        ) {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => {
                        const val = e.target.value;
                        setRpeModified(true);
                        setRirModified(true);
                        if (val === "") {
                          setRpe(null);
                          setRir(null);
                        } else {
                          const numVal = parseInt(val);
                          const clampedRpe = Math.min(10, Math.max(0, numVal));
                          setRpe(clampedRpe);
                          setRir(10 - clampedRpe);
                        }
                        setHasChanges(true);
                        setSaveError(null);
                      }}
                      className="w-16 rounded border border-gray-300 bg-white p-1 text-xs text-navy-700 placeholder:text-gray-400 dark:border-gray-700 dark:bg-navy-900 dark:text-white dark:[color-scheme:dark] dark:placeholder:text-gray-700 md:w-20 md:p-2 md:text-sm"
                    />
                  </div>

                  {/* Rest Time */}
                  <DurationInput
                    value={restSeconds}
                    onChange={(val) => {
                      setRestSeconds(val);
                      setRestSecondsModified(true);
                      setHasChanges(true);
                      setSaveError(null);
                    }}
                    label="Rest"
                    alignLabel
                    hideSpinner
                  />
                </div>
              </div>

              {/* Notes */}
              <div className="flex gap-3">
                <span className="w-8 text-xs font-medium text-gray-500"></span>
                <div className="flex flex-1 flex-col gap-1">
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300 md:text-sm">
                    Notes
                  </label>
                  <textarea
                    placeholder="Add notes..."
                    value={notes}
                    onChange={(e) => {
                      setNotes(e.target.value);
                      setHasChanges(true);
                      setSaveError(null);
                    }}
                    rows={2}
                    className="w-full resize-none rounded border border-gray-300 bg-white p-1 text-xs text-navy-700 placeholder:text-gray-400 dark:border-gray-700 dark:bg-navy-900 dark:text-white dark:placeholder:text-gray-700 md:p-2 md:text-sm"
                  />
                </div>
              </div>
            </div>

            {setKeys.length > 0 ? (
              <div className="space-y-2">
                {setKeys.map((setKey, setIndex) => {
                  const trainingMethod = exercise.training_method;

                  return (
                    <div
                      key={setKey}
                      className="flex justify-between rounded-lg bg-gray-50 px-4 py-2 dark:bg-navy-800"
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-8 text-xs font-medium text-gray-500">
                          {setIndex + 1}.
                        </span>

                        <div className="flex flex-wrap items-start gap-3">
                          {/* Weight Exercise Fields */}
                          {trainingMethod === "weight" && (
                            <>
                              {/* Reps */}
                              <div className="flex flex-col gap-1">
                                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 md:text-sm">
                                  Reps *
                                </label>
                                <input
                                  type="number"
                                  min="1"
                                  placeholder="0"
                                  value={setsData[setKey]?.reps ?? ""}
                                  onKeyDown={(e) => {
                                    if (
                                      e.key === "-" ||
                                      e.key === "+" ||
                                      e.key === "e" ||
                                      e.key === "E" ||
                                      e.key === "."
                                    ) {
                                      e.preventDefault();
                                    }
                                  }}
                                  onChange={(e) =>
                                    handleSetChange(
                                      setKey,
                                      "reps",
                                      e.target.value
                                    )
                                  }
                                  className={`w-16 rounded border bg-white p-1 text-xs text-navy-700 placeholder:text-gray-400 dark:bg-navy-900 dark:text-white dark:[color-scheme:dark] dark:placeholder:text-gray-700 md:w-20 md:p-2 md:text-sm ${
                                    validationErrors[`${setKey}_reps`]
                                      ? "border-red-500 dark:border-red-400"
                                      : "border-gray-300 dark:border-gray-700"
                                  }`}
                                />
                                {validationErrors[`${setKey}_reps`] && (
                                  <div className="text-xs text-red-600 dark:text-red-400 md:text-sm">
                                    {validationErrors[`${setKey}_reps`]}
                                  </div>
                                )}
                              </div>

                              {/* Weight */}
                              <div className="flex flex-col gap-1">
                                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 md:text-sm">
                                  Weight (kg)
                                </label>
                                <input
                                  type="number"
                                  min="1"
                                  step="0.5"
                                  placeholder="-"
                                  value={setsData[setKey]?.weight || ""}
                                  onKeyDown={(e) => {
                                    if (
                                      e.key === "-" ||
                                      e.key === "+" ||
                                      e.key === "e" ||
                                      e.key === "E"
                                    ) {
                                      e.preventDefault();
                                    }
                                  }}
                                  onChange={(e) =>
                                    handleSetChange(
                                      setKey,
                                      "weight",
                                      e.target.value
                                    )
                                  }
                                  className={`w-16 rounded border bg-white p-1 text-xs text-navy-700 placeholder:text-gray-400 dark:bg-navy-900 dark:text-white dark:[color-scheme:dark] dark:placeholder:text-gray-700 md:w-20 md:p-2 md:text-sm ${
                                    validationErrors[`${setKey}_weight`]
                                      ? "border-red-500 dark:border-red-400"
                                      : "border-gray-300 dark:border-gray-700"
                                  }`}
                                />
                                {validationErrors[`${setKey}_weight`] && (
                                  <div className="text-xs text-red-600 dark:text-red-400 md:text-sm">
                                    {validationErrors[`${setKey}_weight`]}
                                  </div>
                                )}
                              </div>
                            </>
                          )}

                          {/* Resistance Band Exercise Fields */}
                          {trainingMethod === "resistance_band" && (
                            <>
                              {/* Reps */}
                              <div className="flex flex-col gap-1">
                                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 md:text-sm">
                                  Reps *
                                </label>
                                <input
                                  type="number"
                                  min="1"
                                  placeholder="0"
                                  value={setsData[setKey]?.reps ?? ""}
                                  onKeyDown={(e) => {
                                    if (
                                      e.key === "-" ||
                                      e.key === "+" ||
                                      e.key === "e" ||
                                      e.key === "E" ||
                                      e.key === "."
                                    ) {
                                      e.preventDefault();
                                    }
                                  }}
                                  onChange={(e) =>
                                    handleSetChange(
                                      setKey,
                                      "reps",
                                      e.target.value
                                    )
                                  }
                                  className={`w-16 rounded border bg-white p-1 text-xs text-navy-700 placeholder:text-gray-400 dark:bg-navy-900 dark:text-white dark:[color-scheme:dark] dark:placeholder:text-gray-700 md:w-20 md:p-2 md:text-sm ${
                                    validationErrors[`${setKey}_reps`]
                                      ? "border-red-500 dark:border-red-400"
                                      : "border-gray-300 dark:border-gray-700"
                                  }`}
                                />
                                {validationErrors[`${setKey}_reps`] && (
                                  <div className="text-xs text-red-600 dark:text-red-400 md:text-sm">
                                    {validationErrors[`${setKey}_reps`]}
                                  </div>
                                )}
                              </div>

                              {/* Resistance Level */}
                              <div className="flex flex-col gap-1">
                                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 md:text-sm">
                                  Resistance
                                </label>
                                <select
                                  value={
                                    setsData[setKey]?.resistance_level ?? ""
                                  }
                                  onChange={(e) =>
                                    handleSetChange(
                                      setKey,
                                      "resistance_level",
                                      e.target.value
                                    )
                                  }
                                  className={`w-42 appearance-none rounded border bg-white bg-select-chevron bg-[length:1rem_1rem] bg-[position:right_0.25rem_center] bg-no-repeat p-1 pr-6 text-xs text-navy-700 dark:bg-navy-900 dark:text-white md:w-52 md:p-2 md:pr-7 md:text-sm ${
                                    validationErrors[
                                      `${setKey}_resistance_level`
                                    ]
                                      ? "border-red-500 dark:border-red-400"
                                      : "border-gray-300 dark:border-gray-700"
                                  }`}
                                >
                                  <option value="">-</option>
                                  {getResistanceBandLevelOptions().map(
                                    (option) => (
                                      <option
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </option>
                                    )
                                  )}
                                </select>
                                {validationErrors[
                                  `${setKey}_resistance_level`
                                ] && (
                                  <div className="text-xs text-red-600 dark:text-red-400 md:text-sm">
                                    {
                                      validationErrors[
                                        `${setKey}_resistance_level`
                                      ]
                                    }
                                  </div>
                                )}
                              </div>
                            </>
                          )}

                          {/* Timed Exercise Fields */}
                          {trainingMethod === "timed" && (
                            <DurationInput
                              value={setsData[setKey]?.duration_seconds ?? null}
                              onChange={(val) =>
                                handleSetChange(setKey, "duration_seconds", val)
                              }
                              label="Duration"
                              required
                              error={
                                validationErrors[`${setKey}_duration_seconds`]
                              }
                              hideSpinner
                            />
                          )}
                        </div>
                      </div>

                      {setKeys.length > 1 && (
                        <button
                          onClick={() => removeSet(setKey)}
                          className="text-red-400 hover:text-red-600 dark:hover:text-red-400"
                        >
                          <MdDelete className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="py-2 text-center text-xs text-gray-500">
                No sets added yet
              </div>
            )}

            <div className="flex items-center justify-between gap-2">
              <button
                onClick={addSet}
                className="flex items-center gap-1 py-1 text-sm text-biafit-primary hover:text-biafit-secondary"
              >
                <MdAdd className="h-4 w-4" />
                Add Set
              </button>
              {hasChanges && (
                <button
                  onClick={initiatesSave}
                  disabled={saving}
                  className="flex items-center gap-2 rounded bg-biafit-primary px-3 py-1 text-sm text-white hover:bg-biafit-secondary disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              )}
            </div>

            {saveError && (
              <div className="rounded bg-red-50 p-2 text-xs text-red-600 dark:bg-red-900/20 dark:text-red-400">
                {saveError}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Persistence Confirmation Modal */}
      <PersistentActionConfirmationModal
        isOpen={showPersistenceModal}
        onClose={() => setShowPersistenceModal(false)}
        onConfirm={saveSetsData}
        actionType="save"
        exerciseName={exercise.name}
        loading={saving}
      />
    </div>
  );
};

export default SortableExerciseItem;
